import "./Navbar.css";

import { useEffect, useState } from "react";
import {
  Link,
  useLocation,
  useNavigate,
} from "react-router-dom";

import {
  FaBars,
  FaCalendarAlt,
  FaTimes,
} from "react-icons/fa";

import logo from "../assets/logo.png";

const enlaces = [
  {
    id: "nosotros",
    texto: "Nosotros",
  },
  {
    id: "inicio",
    texto: "Proyecto",
  },
  {
    id: "apartamentos",
    texto: "Apartamentos",
  },
  {
    id: "galeria",
    texto: "Galería",
  },
  {
    id: "amenidades",
    texto: "Amenidades",
  },
  {
    id: "contacto",
    texto: "Contacto",
  },
];

function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuAbierto, setMenuAbierto] = useState(false);

  const location = useLocation();
  const navigate = useNavigate();

  /* Detectar desplazamiento */

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 35);
    };

    handleScroll();

    window.addEventListener("scroll", handleScroll, {
      passive: true,
    });

    return () => {
      window.removeEventListener(
        "scroll",
        handleScroll
      );
    };
  }, []);

  /* Cerrar menú al cambiar de página */

  useEffect(() => {
    setMenuAbierto(false);
  }, [location.pathname, location.hash]);

  /* Ir a una sección cuando se llega desde otra ruta */

  useEffect(() => {
    if (
      location.pathname === "/" &&
      location.hash
    ) {
      const idSeccion = location.hash.replace("#", "");

      const temporizador = setTimeout(() => {
        const seccion =
          document.getElementById(idSeccion);

        if (seccion) {
          seccion.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
        }
      }, 150);

      return () => clearTimeout(temporizador);
    }
  }, [location.pathname, location.hash]);

  /* Bloquear el fondo cuando el menú está abierto */

  useEffect(() => {
    document.body.style.overflow = menuAbierto
      ? "hidden"
      : "";

    const cerrarConEscape = (event) => {
      if (event.key === "Escape") {
        setMenuAbierto(false);
      }
    };

    window.addEventListener(
      "keydown",
      cerrarConEscape
    );

    return () => {
      document.body.style.overflow = "";

      window.removeEventListener(
        "keydown",
        cerrarConEscape
      );
    };
  }, [menuAbierto]);

  /* Cerrar menú si la pantalla vuelve a escritorio */

  useEffect(() => {
    const revisarAncho = () => {
      if (window.innerWidth > 980) {
        setMenuAbierto(false);
      }
    };

    window.addEventListener("resize", revisarAncho);

    return () => {
      window.removeEventListener(
        "resize",
        revisarAncho
      );
    };
  }, []);

  const irASeccion = (event, idSeccion) => {
    event.preventDefault();

    setMenuAbierto(false);

    if (location.pathname !== "/") {
      navigate(`/#${idSeccion}`);
      return;
    }

    navigate(`/#${idSeccion}`);

    setTimeout(() => {
      const seccion =
        document.getElementById(idSeccion);

      if (seccion) {
        seccion.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }
    }, 40);
  };

  const alternarMenu = () => {
    setMenuAbierto((estadoAnterior) => {
      return !estadoAnterior;
    });
  };

  return (
    <header
      className={[
        "navbar",
        scrolled ? "navbar-scrolled" : "",
        menuAbierto ? "navbar-open" : "",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <div className="navbar-container">
        {/* LOGO */}

        <a
          href="/#inicio"
          className="navbar-logo"
          onClick={(event) =>
            irASeccion(event, "inicio")
          }
          aria-label="Ir al inicio"
        >
          <div className="navbar-logo-image">
            <img
              src={logo}
              alt="Logo de YANNA A&P Constructions"
            />
          </div>

          <div className="navbar-brand">
            <h2>YANNA A&amp;P</h2>
            <span>CONSTRUCTIONS</span>
          </div>
        </a>

        {/* MENÚ DE ESCRITORIO Y MÓVIL */}

        <nav
          id="navbar-navigation"
          className={`navbar-menu ${
            menuAbierto
              ? "navbar-menu-visible"
              : ""
          }`}
          aria-label="Navegación principal"
        >
          <div className="navbar-mobile-header">
            <div>
              <span>MENÚ PRINCIPAL</span>
              <strong>YANNA A&amp;P</strong>
            </div>

            <button
              type="button"
              className="navbar-mobile-close"
              onClick={() => setMenuAbierto(false)}
              aria-label="Cerrar menú"
            >
              <FaTimes />
            </button>
          </div>

          <div className="navbar-menu-links">
            {enlaces.map((enlace, index) => (
              <a
                key={enlace.id}
                href={`/#${enlace.id}`}
                onClick={(event) =>
                  irASeccion(event, enlace.id)
                }
              >
                <span className="navbar-link-number">
                  {String(index + 1).padStart(2, "0")}
                </span>

                <span>{enlace.texto}</span>
              </a>
            ))}
          </div>

          {/* Botón que solo aparece dentro del menú móvil */}

          <Link
            to="/reserva"
            className="navbar-mobile-reserve"
            onClick={() => setMenuAbierto(false)}
          >
            <FaCalendarAlt />
            Agendar visita
          </Link>

          <div className="navbar-mobile-contact">
            <span>ATENCIÓN DIRECTA</span>

            <a href="tel:+18299623399">
              (829) 962-3399
            </a>
          </div>
        </nav>

        {/* BOTÓN DE ESCRITORIO */}

        <Link
          to="/reserva"
          className="navbar-reserve-link"
        >
          <FaCalendarAlt />
          Agendar visita
        </Link>

        {/* BOTÓN HAMBURGUESA */}

        <button
          type="button"
          className="navbar-toggle"
          onClick={alternarMenu}
          aria-label={
            menuAbierto
              ? "Cerrar menú"
              : "Abrir menú"
          }
          aria-expanded={menuAbierto}
          aria-controls="navbar-navigation"
        >
          <span>MENÚ</span>

          <div>
            {menuAbierto ? <FaTimes /> : <FaBars />}
          </div>
        </button>
      </div>

      {/* FONDO OSCURECIDO DEL MENÚ */}

      <button
        type="button"
        className={`navbar-overlay ${
          menuAbierto
            ? "navbar-overlay-visible"
            : ""
        }`}
        onClick={() => setMenuAbierto(false)}
        aria-label="Cerrar menú"
        tabIndex={menuAbierto ? 0 : -1}
      />
    </header>
  );
}

export default Navbar;